// src/components/Customers/CustomerList.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { PlusCircle, ArrowUpDown, Search } from "lucide-react";
import CustomerForm from "./CustomerForm";
import CustomerDetails from "./CustomerDetails";
import { checkAuth, handleAuthError } from "../../utils/auth";

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filters, setFilters] = useState({
    tipo: "",
    status: "",
    uf: "",
    municipio: "",
  });

  // Refs para estados e municípios
  const statesRef = useRef({});
  const municipalitiesRef = useRef({});
  const [states, setStates] = useState({});
  const [municipalities, setMunicipalities] = useState({});

  const fetchStates = useCallback(async () => {
    try {
      const response = await fetch(
        "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
      );
      if (!response.ok)
        throw new Error(`Erro ao buscar estados: ${response.status}`);
      const data = await response.json();
      const statesMap = {};
      data.forEach((state) => {
        statesMap[state.id] = { sigla: state.sigla, nome: state.nome };
      });
      statesRef.current = statesMap;
      setStates(statesMap);
    } catch (err) {
      console.error(err);
      setError("Erro ao carregar estados. Por favor, tente novamente.");
    }
  }, []);

  const fetchMunicipalities = useCallback(async (estadoId) => {
    if (municipalitiesRef.current[estadoId]) {
      setMunicipalities((prev) => ({
        ...prev,
        [estadoId]: municipalitiesRef.current[estadoId],
      }));
      return;
    }
    try {
      const response = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estadoId}/municipios`
      );
      if (!response.ok)
        throw new Error(
          `Erro ao buscar municípios do estado ${estadoId}: ${response.status}`
        );
      const data = await response.json();
      const municipalitiesMap = {};
      data.forEach((municipio) => {
        municipalitiesMap[municipio.id] = municipio.nome;
      });
      municipalitiesRef.current[estadoId] = municipalitiesMap;
      setMunicipalities((prev) => ({ ...prev, [estadoId]: municipalitiesMap }));
    } catch (err) {
      console.error(err);
      municipalitiesRef.current[estadoId] = {};
      setMunicipalities((prev) => ({ ...prev, [estadoId]: {} }));
    }
  }, []);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  const fetchCustomers = useCallback(async () => {
    try {
      if (!checkAuth()) {
        window.location.href = "/login";
        return;
      }
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      if (search) queryParams.append("search", search);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/customers?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      const data = await response.json();
      setCustomers(data.data || []);

      // Buscar municípios faltantes
      const uniqueMunicipios = data.data.reduce((acc, customer) => {
        const estadoId = customer.ESTADO_CODIGO_IBGE;
        const municipioId = customer.MUNICIPIO_CODIGO_IBGE;
        if (
          estadoId &&
          municipioId &&
          !municipalitiesRef.current[estadoId]?.[municipioId]
        ) {
          acc.add(estadoId);
        }
        return acc;
      }, new Set());
      uniqueMunicipios.forEach((estadoId) => {
        fetchMunicipalities(estadoId);
      });
    } catch (error) {
      if (!handleAuthError(error)) {
        setError("Erro ao carregar os clientes. Por favor, tente novamente.");
      }
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [filters, search, fetchMunicipalities]);

  useEffect(() => {
    fetchCustomers();
  }, [filters, search, fetchCustomers]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    setIsDetailsModalOpen(true);
  };

  const getSortedCustomers = () => {
    if (!sortConfig.key) return customers;
    return [...customers].sort((a, b) => {
      let aValue;
      let bValue;
      if (sortConfig.key === "UF") {
        aValue = states[a.ESTADO_CODIGO_IBGE]?.sigla || "";
        bValue = states[b.ESTADO_CODIGO_IBGE]?.sigla || "";
      } else if (sortConfig.key === "MUNICIPIO") {
        aValue =
          municipalities[a.ESTADO_CODIGO_IBGE]?.[a.MUNICIPIO_CODIGO_IBGE] || "";
        bValue =
          municipalities[b.ESTADO_CODIGO_IBGE]?.[b.MUNICIPIO_CODIGO_IBGE] || "";
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }
      aValue = aValue ? aValue.toString().toLowerCase() : "";
      bValue = bValue ? bValue.toString().toLowerCase() : "";
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const formatStatus = (status) => {
    const statusMap = {
      0: { label: "Inativo", class: "bg-red-100 text-red-800" },
      1: { label: "Ativo", class: "bg-green-100 text-green-800" },
      2: { label: "Suspenso", class: "bg-yellow-100 text-yellow-800" },
      3: { label: "Em análise", class: "bg-blue-100 text-blue-800" },
      4: { label: "Regular", class: "bg-green-100 text-green-800" },
    };
    return (
      statusMap[status] || {
        label: "Desconhecido",
        class: "bg-gray-100 text-gray-800",
      }
    );
  };

  const formatCpfCnpj = (value) => {
    if (!value) return "-";
    const digits = value.replace(/\D/g, "");
    if (digits.length === 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else if (digits.length === 14) {
      return digits.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5"
      );
    }
    return value;
  };

  const CustomerCard = ({ customer }) => {
    const estado = states[customer.ESTADO_CODIGO_IBGE];
    const municipio = municipalities[customer.ESTADO_CODIGO_IBGE]
      ? municipalities[customer.ESTADO_CODIGO_IBGE][
          customer.MUNICIPIO_CODIGO_IBGE
        ]
      : null;
    return (
      <div
        className="bg-white rounded-lg shadow-sm p-4 mb-4 cursor-pointer"
        onClick={() => handleViewDetails(customer)}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-gray-900">
            {customer.RAZAO_SOCIAL}
          </h3>
          <span
            className={`inline-block px-2 py-1 text-xs rounded-full ${
              formatStatus(customer.STATUS).class
            }`}
          >
            {formatStatus(customer.STATUS).label}
          </span>
        </div>
        <p className="text-sm text-gray-500">#{customer.CODIGO}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mt-3">
          <div>
            <p className="text-gray-500">CNPJ/CPF</p>
            <p className="text-gray-900">{formatCpfCnpj(customer.CNPJ)}</p>
          </div>
          <div>
            <p className="text-gray-500">UF</p>
            <p className="text-gray-900">
              {estado ? estado.sigla : "Carregando..."}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Município</p>
            <p className="text-gray-900">
              {municipio ? municipio : "Carregando..."}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Última Compra</p>
            <p className="text-gray-900">
              {customer.DATA_ULTIMA_COMPRA
                ? new Date(customer.DATA_ULTIMA_COMPRA).toLocaleDateString()
                : "-"}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const SortableHeader = ({ label, field }) => (
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group"
      onClick={() => requestSort(field)}
    >
      <div className="flex items-center">
        {label}
        <ArrowUpDown
          className={`w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 ${
            sortConfig.key === field ? "opacity-100" : ""
          }`}
        />
      </div>
    </th>
  );

  const renderCustomers = () => {
    const sortedCustomers = getSortedCustomers();
    if (loading) {
      return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="p-4">
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
            <p className="mb-2">{error}</p>
            <button
              onClick={fetchCustomers}
              className="bg-red-100 px-4 py-2 rounded-md hover:bg-red-200 transition-colors w-full"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }
    return (
      <>
        {/* Mobile View */}
        <div className="sm:hidden">
          {sortedCustomers.map((customer) => (
            <CustomerCard key={customer.CODIGO} customer={customer} />
          ))}
        </div>
        {/* Desktop View */}
        <div className="hidden sm:block">
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <SortableHeader label="Código" field="CODIGO" />
                  <SortableHeader label="Razão Social" field="RAZAO_SOCIAL" />
                  <SortableHeader label="CNPJ/CPF" field="CNPJ" />
                  <SortableHeader label="UF" field="UF" />
                  <SortableHeader label="Município" field="MUNICIPIO" />
                  <SortableHeader label="Status" field="STATUS" />
                  <SortableHeader
                    label="Última Compra"
                    field="DATA_ULTIMA_COMPRA"
                  />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedCustomers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      Nenhum cliente encontrado
                    </td>
                  </tr>
                ) : (
                  sortedCustomers.map((customer) => {
                    const estado = states[customer.ESTADO_CODIGO_IBGE];
                    const municipio = municipalities[
                      customer.ESTADO_CODIGO_IBGE
                    ]
                      ? municipalities[customer.ESTADO_CODIGO_IBGE][
                          customer.MUNICIPIO_CODIGO_IBGE
                        ]
                      : null;
                    return (
                      <tr key={customer.CODIGO} className="hover:bg-gray-50">
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 cursor-pointer hover:text-blue-800"
                          onClick={() => handleViewDetails(customer)}
                        >
                          {customer.CODIGO}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {customer.RAZAO_SOCIAL}
                          </div>
                          <div className="text-sm text-gray-500">
                            {customer.NOME_FANTASIA}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCpfCnpj(customer.CNPJ)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {estado ? estado.sigla : "Carregando..."}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {municipio ? municipio : "Carregando..."}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              formatStatus(customer.STATUS).class
                            }`}
                          >
                            {formatStatus(customer.STATUS).label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {customer.DATA_ULTIMA_COMPRA
                            ? new Date(
                                customer.DATA_ULTIMA_COMPRA
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header e Filtros com Sticky */}
      <div className="sticky top-0 bg-gray-50 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl font-semibold text-gray-900">Clientes</h1>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-red-700 transition-colors w-full sm:w-auto justify-center"
            >
              <PlusCircle className="w-5 h-5 mr-2" />
              Novo Cliente
            </button>
          </div>
          {/* Filtros */}
          <div className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-shadow bg-white"
                />
              </div>
              <select
                value={filters.tipo}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, tipo: e.target.value }))
                }
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              >
                <option value="">Todos os tipos</option>
                <option value="F">Física</option>
                <option value="R">Revenda</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              >
                <option value="">Todos os status</option>
                <option value="4">Regular</option>
                <option value="1">Ativo</option>
                <option value="0">Inativo</option>
                <option value="2">Suspenso</option>
                <option value="3">Em análise</option>
              </select>
              {/* Filtro de Estado utilizando o código IBGE */}
              <select
                value={filters.uf}
                onChange={(e) => {
                  const selectedUf = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    uf: selectedUf,
                    municipio: "",
                  }));
                  if (selectedUf) {
                    fetchMunicipalities(parseInt(selectedUf));
                  }
                }}
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              >
                <option value="">Todos os UFs</option>
                {Object.entries(states).map(([estadoId, state]) => (
                  <option key={estadoId} value={estadoId}>
                    {state.nome} ({state.sigla})
                  </option>
                ))}
              </select>
              {/* Filtro de Municípios com código IBGE */}
              <select
                value={filters.municipio}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, municipio: e.target.value }))
                }
                disabled={!filters.uf || !municipalitiesRef.current[filters.uf]}
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              >
                <option value="">Todos os Municípios</option>
                {filters.uf &&
                  municipalities[filters.uf] &&
                  Object.entries(municipalities[filters.uf]).map(
                    ([municipioId, municipioNome]) => (
                      <option key={municipioId} value={municipioId}>
                        {municipioNome}
                      </option>
                    )
                  )}
              </select>
            </div>
          </div>
        </div>
      </div>
      {/* Conteúdo */}
      <div className="max-w-7xl mx-auto px-4 py-6">{renderCustomers()}</div>
      {isAddModalOpen && (
        <CustomerForm
          onClose={() => setIsAddModalOpen(false)}
          onSave={() => {
            fetchCustomers();
            setIsAddModalOpen(false);
          }}
        />
      )}
      {isDetailsModalOpen && selectedCustomer && (
        <CustomerDetails
          customer={selectedCustomer}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedCustomer(null);
          }}
        />
      )}
    </div>
  );
};

export default CustomerList;
